import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { supabase, Profile } from '@/lib/supabase';
import { Search, UserPlus, Filter, Award, Plus, Minus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export default function UsersPage() {
  const { profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [pointsType, setPointsType] = useState<'add' | 'remove'>('add');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!authLoading && profile?.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [profile, authLoading, navigate]);

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchUsers();
    }
  }, [profile]);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (data) {
      setUsers(data);
    }
    setLoading(false);
  };

  const filteredUsers = users.filter(user =>
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenPointsDialog = (user: Profile, type: 'add' | 'remove') => {
    setSelectedUser(user);
    setPointsType(type);
    setPointsAmount('');
    setPointsReason('');
    setShowPointsDialog(true);
  };

  const handleAssignPoints = async () => {
    if (!selectedUser || !pointsAmount || !pointsReason) {
      toast({
        title: 'Errore',
        description: 'Compila tutti i campi',
        variant: 'destructive',
      });
      return;
    }

    const points = parseInt(pointsAmount);
    if (isNaN(points) || points <= 0) {
      toast({
        title: 'Errore',
        description: 'Inserisci un numero valido di punti',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);
    try {
      const { error } = await supabase
        .from('reward_points_transactions')
        .insert({
          student_id: selectedUser.id,
          admin_id: profile?.id,
          points: pointsType === 'add' ? points : -points,
          reason: pointsReason,
          type: 'adjusted',
        });

      if (error) throw error;

      toast({
        title: 'Successo',
        description: `${points} punti ${pointsType === 'add' ? 'aggiunti' : 'rimossi'} con successo`,
      });

      setShowPointsDialog(false);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile assegnare i punti',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  if (authLoading || profile?.role !== 'admin') {
    return null;
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Gestione Utenti
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci tutti gli utenti della piattaforma
            </p>
          </div>
          <Button>
            <UserPlus className="w-4 h-4 mr-2" />
            Aggiungi utente
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Cerca per nome o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline">
                <Filter className="w-4 h-4 mr-2" />
                Filtri
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utente</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead>Punti Premio</TableHead>
                      <TableHead>Data registrazione</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                              {user.full_name?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <span className="font-medium">{user.full_name || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{user.email}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              user.role === 'admin'
                                ? 'destructive'
                                : user.role === 'docente'
                                ? 'default'
                                : 'secondary'
                            }
                            className="capitalize"
                          >
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-yellow-600" />
                            <span className="font-semibold text-yellow-600">
                              {user.reward_points || 0}
                            </span>
                            {user.role === 'studente' && (
                              <div className="flex gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleOpenPointsDialog(user, 'add')}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => handleOpenPointsDialog(user, 'remove')}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {new Date(user.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            Dettagli
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showPointsDialog} onOpenChange={setShowPointsDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {pointsType === 'add' ? 'Aggiungi' : 'Rimuovi'} Punti Premio
              </DialogTitle>
              <DialogDescription>
                {pointsType === 'add' ? 'Aggiungi' : 'Rimuovi'} punti premio per {selectedUser?.full_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="points">Numero di punti</Label>
                <Input
                  id="points"
                  type="number"
                  min="1"
                  placeholder="Es. 10"
                  value={pointsAmount}
                  onChange={(e) => setPointsAmount(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  1 punto = 1 euro di sconto
                </p>
              </div>
              <div>
                <Label htmlFor="reason">Motivo</Label>
                <Textarea
                  id="reason"
                  placeholder="Descrivi il motivo dell'assegnazione punti..."
                  value={pointsReason}
                  onChange={(e) => setPointsReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowPointsDialog(false)}
                disabled={processing}
              >
                Annulla
              </Button>
              <Button onClick={handleAssignPoints} disabled={processing}>
                {processing ? 'Elaborazione...' : 'Conferma'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
